var AbsoluteShowingQueueSupport = require("enyo-perf/AbsoluteShowingQueueSupport"),
    PlayerApi                   = require("radiant-mediaplayer-iframe-api").default,
    config                      = require("app/config"),
    get                         = require("object-path").get,
    kind                        = require("enyo/kind");

/**
 * When rendered, this control creates a Radiant Media Player running in an
 * iframe. It exposes a PlayerApi to interact with it.
 *
 * Player setup is applied using the `playerSetup` property and event
 * listeners can be supplied using the `playerEventListeners` property.
 */
module.exports = kind(/** @lends  app.ui.radiant.MediaPlayer.prototype */ {

    /**
     * @private
     */
    name: "app.ui.radiant.MediaPlayer",

    /**
     * @private
     */
    mixins: [AbsoluteShowingQueueSupport],

    /**
     * @private
     */
    classes: "app-ui-radiant-media-player",

    /**
     * An iframe for RadiantMP Iframe Player.
     *
     * @type  {HTMLIFrameElement}
     * @private
     */
    iframe: null,

    /**
     * @type {PlayerApi}
     * @private
     */
    playerApi: null,

    /**
     * An array of registered event listeners.
     *
     * @type {array<array>}
     * @private
     */
    registeredPlayerEventListeners: null,

    // Public API
    // --------------------------------

    /**
     * Setup configuration for the player.
     *
     * @type {Object}
     * @public
     */
    playerSetup: null,

    /**
     * An list of event listeners to attach to the player. Each listener should
     * be applied as a pair of two values; the event name and the listener
     * function.
     *
     *
     * @type {array<array>}
     * @public
     */
    playerEventListeners: null,

    /**
     * Whether we have a player available.
     *
     * @returns {boolean} `true` if available, `false` otherwise
     * @public
     */
    hasPlayer: function() {
        return Boolean(this.get("playerApi"));
    },

    /**
     * Returns the PlayerApi if available.
     *
     * @returns {PlayerApi|null} `PlayerApi` if available, `null` otherwise
     * @public
     */
    getPlayerApi: function() {
        return this.get("playerApi") || null;
    },

    /**
     * Initializes the RadiantMP Player.
     *
     * @public
     */
    createPlayer: function() {
        var node,
            iframe,
            playerSetup,
            playerEventListeners,
            playerApi;

        if (this.hasPlayer()) {
            return;
        }

        node                 = this.hasNode();
        playerSetup          = this.get("playerSetup");
        playerEventListeners = this.get("playerEventListeners");

        if (!node || !playerSetup) {
            return;
        }

        iframe    = this.createPlayerIframe();

        if (iframe) {
            playerApi = new PlayerApi(iframe);
            playerApi.loaded.then(this.bindSafely(function() {
                if (Array.isArray(playerEventListeners)) {
                    this.addPlayerEventListeners(playerEventListeners);
                }
                playerApi.setup(playerSetup);
            }));
            this.set("playerApi", playerApi);
        }
        else {
            this.bubble("onMediaPlayerError", {
                type:    "player_setup_error",
                message: "Failed to create player iframe"
            });
        }
    },

    /**
     * Destroys the RadiantMP player and removes the iframe.
     *
     * @public
     */
    destroyPlayer: function() {
        var playerApi = this.get("playerApi"),
            iframe    = this.get("iframe");

        // Tell Radiant Media Player to destroy before removing the iframe,
        // because the player API needs to be able to send a message to the
        // player running inside the iframe.
        if (playerApi) {
            playerApi.stop();
            this.removePlayerEventListeners();
            playerApi.destroy();
            this.set("playerApi", null);
        }

        // Now that the player has received it's command to destroy,.we can
        // safely remove the iframe.
        if (iframe) {
            iframe.parentNode && iframe.remove();
            this.set("iframe", null);
        }
    },

    // Player Creation
    // --------------------------------

    /**
     * Make sure existing Radiant MediaPlayer instances are torn down before
     * rendering a new container.
     *
     * @private
     */
    render: kind.inherit(function(sup) {
        return function() {
            // Destroy the player. This stops playback and synchronously
            // detaches event listeners, but the destroy sequence of Radiant
            // Media Player after that is asynchronous.
            this.hasPlayer() && this.destroyPlayer();

            // Render as usual
            sup.apply(this, arguments);
        };
    }),

    /**
     * @private
     */
    rendered: kind.inherit(function(sup) {
        return function() {
            // Render the new player before reflowing
            !this.hasPlayer() && this.createPlayer();

            // Reflow as usual
            sup.apply(this, arguments);
        };
    }),

    /**
     * Creates an Iframe for the player to run in.
     *
     * @returns {HTMLIFrameElement|null} The iframe if created, `null` otherwise
     * @private
     */
    createPlayerIframe: function() {
        var node      = this.hasNode(),
            playerUrl = config.radiantMediaPlayer.iframePlayerUrl,
            iframe    = null;

        if (node && playerUrl) {
            playerUrl = new URL(playerUrl);

            if (typeof __Vikaru !== "undefined" && !__Vikaru.allowTracking) {
                playerUrl.searchParams.set("sentry", "false");
            }
            else if (!this.app.$.sentryIOService.hasLoadConsent()) {
                playerUrl.searchParams.set("sentry", "false");
            }
            else if (!get(config, "sentry.enabled", false)) {
                playerUrl.searchParams.set("sentry", "false");
            }
            else {
                playerUrl.searchParams.set("sentry", "true");
            }

            playerUrl.searchParams.set("v", config.radiantMediaPlayer.version);

            iframe = document.createElement("iframe");
            iframe.setAttribute("frameborder", "0");
            iframe.setAttribute("allowfullcreen", "");
            iframe.setAttribute("allow", "autoplay; fullscreen; encrypted-media;");
            iframe.setAttribute("src", playerUrl.toString());
            node.appendChild(iframe);
            this.set("iframe", iframe);
        }

        return iframe;
    },

    /**
     * Add event listeners to the player.
     *
     * @param {Array<Array>} listeners  The event listeners to add
     * @private
     */
    addPlayerEventListeners: function(listeners) {
        var playerApi                      = this.get("playerApi"),
            registeredPlayerEventListeners = this.registeredPlayerEventListeners || [];

        if (playerApi && listeners) {
            listeners.forEach(function(listener) {
                playerApi.on(listener[0], listener[1]);
                registeredPlayerEventListeners.push(listener);
            });
            this.registeredPlayerEventListeners = registeredPlayerEventListeners;
        }
    },

    // Player Destruction
    // --------------------------------

    /**
     * Since we added our own eventListener on render, we also need to remove it
     * again.
     *
     * @private
     */
    beforeTeardown: kind.inherit(function(sup) {
        return function() {
            this.hasPlayer() && this.destroyPlayer();
            sup.apply(this, arguments);
        };
    }),


    /**
     * Removes event listeners from the player.
     *
     * @private
     */
    removePlayerEventListeners: function() {
        var playerApi = this.get("playerApi"),
            listeners = this.registeredPlayerEventListeners || [];

        if (playerApi && listeners) {
            listeners.forEach(function(listener) {
                playerApi.off(listener[0], listener[1]);
            });
            this.registeredPlayerEventListeners = [];
        }
    },

    /**
     * Destroy the Radiant MediaPlayer when this instance gets destroyed.
     *
     * @private
     */
    destroy: kind.inherit(function(sup) {
        return function() {
            this.hasPlayer() && this.destroyPlayer();
            sup.apply(this, arguments);
        };
    })

});
